import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  Snackbar,
  InputAdornment,
  Avatar,
  Link as MuiLink,
  Paper
} from '@mui/material';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  serverTimestamp, 
  doc, 
  getDoc, 
  deleteDoc, 
  orderBy, 
  setDoc, 
  updateDoc, 
  limit,
  getCountFromServer,
  writeBatch,
  FieldValue
} from 'firebase/firestore';
import { db, auth, googleProvider } from '../firebase';
import { signInWithPopup, onAuthStateChanged } from 'firebase/auth';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import TopBar from './TopBar';
import { searchBookByISBN } from '../api/bookSearch';
import { handleGoogleLogin } from "../services/authService";
import InAppBrowserModal from "./InAppBrowserModal";
import { Link as RouterLink } from 'react-router-dom';

// Fisher-Yates 셔플 알고리즘
const fisherYatesShuffle = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

function BookSearchDetail() { 
  const { isbn: isbnFromParams } = useParams(); 
  const location = useLocation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(auth.currentUser);
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInLibrary, setIsInLibrary] = useState(false);
  const [loadingLibraryStatus, setLoadingLibraryStatus] = useState(true);
  const [addingToLibrary, setAddingToLibrary] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);
  const [inAppBrowserModalOpen, setInAppBrowserModalOpen] = useState(false);
  const [navigationTimeout, setNavigationTimeout] = useState(null);
  const [otherHighlights, setOtherHighlights] = useState([]);
  const [otherMemos, setOtherMemos] = useState([]);
  const [loadingHighlights, setLoadingHighlights] = useState(true);
  const [loadingMemos, setLoadingMemos] = useState(true);
  const [userMap, setUserMap] = useState({});

  // 성능 최적화를 위한 메모이제이션
  const memoizedOtherHighlights = useMemo(() => otherHighlights, [otherHighlights]);
  const memoizedOtherMemos = useMemo(() => otherMemos, [otherMemos]);
  const memoizedUserMap = useMemo(() => userMap, [userMap]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("[BookSearchDetail] Auth state changed:", user ? user.uid : null);
      setCurrentUser(user);
      if (!user) {
        setIsInLibrary(false);
        setLoadingLibraryStatus(false);
      }
    });
    return () => {
      unsubscribe();
      // 컴포넌트 언마운트 시 타이머 정리
      if (navigationTimeout) {
        clearTimeout(navigationTimeout);
      }
    };
  }, [navigationTimeout]);

  useEffect(() => {
    const fetchBookData = async () => {
      if (!isbnFromParams) {
        console.error("[BookSearchDetail] Error: ISBN is missing from URL params.");
        setError("책 정보를 불러올 수 없습니다 (ISBN 누락).");
        setLoading(false);
        setLoadingLibraryStatus(false);
        return;
      }

      console.log(`[BookSearchDetail] Starting book data fetch for ISBN: ${isbnFromParams}`);
      setLoading(true);
      setBook(null);
      setIsInLibrary(false);
      setLoadingLibraryStatus(true);
      setError(null);

      const resolvedIsbn = isbnFromParams.split(' ')[0]; 
      if (!resolvedIsbn) {
        console.error("[BookSearchDetail] Invalid ISBN after cleaning:", isbnFromParams);
        setError("유효하지 않은 ISBN입니다.");
        setLoading(false);
        setLoadingLibraryStatus(false);
        return;
      }
      console.log(`[BookSearchDetail] Using resolved ISBN: ${resolvedIsbn}`);
      console.log(`[BookSearchDetail] Current user:`, currentUser?.uid);
      console.log(`[BookSearchDetail] Book data:`, book);

      let foundBookData = null;

      try {
        console.log(`[BookSearchDetail] Attempt 2: Fetch from Kakao API by ISBN: ${resolvedIsbn}`);
        const apiBookData = await searchBookByISBN(resolvedIsbn); 
        if (apiBookData) {
          foundBookData = { ...apiBookData, isbn: resolvedIsbn };
          console.log("[BookSearchDetail] Found via Kakao API:", foundBookData);
        } else {
          console.warn(`[BookSearchDetail] Book not found via Kakao API for ISBN: ${resolvedIsbn}`);
        }

        if (foundBookData) {
          setBook({ ...foundBookData, isbn: resolvedIsbn });
        } else {
          setError(`책 정보를 찾을 수 없습니다 (ISBN: ${resolvedIsbn}).`);
          setBook(null);
        }

      } catch (error) {
        console.error('[BookSearchDetail] Error fetching book data:', error);
        setError(`책 정보 로딩 중 오류 발생: ${error.message}`);
        setBook(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBookData();
  }, [isbnFromParams]); 

  useEffect(() => {
    const checkLibraryStatus = async () => {
      if (currentUser && book && book.isbn) {
        setLoadingLibraryStatus(true); 
        const resolvedIsbn = (book.isbn || '').split(' ')[0];
        if (!resolvedIsbn) {
            console.warn("[BookSearchDetail] Cannot check library status: No valid ISBN found after cleaning.");
            setIsInLibrary(false);
            setLoadingLibraryStatus(false);
            return;
        }

        const bookDocId = `${currentUser.uid}_${resolvedIsbn}`;
        console.log(`[BookSearchDetail] Checking library status for user: ${currentUser.uid}, Doc ID: ${bookDocId}`);

        try {
          const bookRef = doc(db, 'books', bookDocId);
          const docSnap = await getDoc(bookRef);
          
          const bookIsInLibrary = docSnap.exists(); 
          setIsInLibrary(bookIsInLibrary);
          console.log(`[BookSearchDetail] Book in library check result (doc exists?): ${bookIsInLibrary}`);
        } catch (error) {
          console.error("[BookSearchDetail] Error checking library status:", error);
          setIsInLibrary(false);
        } finally {
          setLoadingLibraryStatus(false);
        }
      } else {
        setIsInLibrary(false);
        setLoadingLibraryStatus(false);
        console.log("[BookSearchDetail] Skipping library check: currentUser or book/ISBN not ready.");
      }
    };

    checkLibraryStatus(); 
  }, [currentUser, book]);

  useEffect(() => {
    const fetchOtherHighlights = async () => {
      if (!book || !book.isbn) {
        setOtherHighlights([]);
        setLoadingHighlights(false);
        return;
      }
      setLoadingHighlights(true);
      const bookIdentifier = (book.isbn || '').split(' ')[0];
      console.log(`[BookSearchDetail] Fetching highlights for ISBN: ${bookIdentifier}`);
      
      try {
        // 여러 필드명으로 하이라이트 검색 (bookId, bookIsbn, isbn)
        const highlightsQuery = query(
          collection(db, 'highlights'),
          where('bookId', '==', bookIdentifier),
          limit(100) // 더 많은 데이터를 가져와서 랜덤 선택
        );
        const highlightsSnapshot = await getDocs(highlightsQuery);
        let allHighlights = highlightsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        console.log(`[BookSearchDetail] Found ${allHighlights.length} highlights with bookId`);
        
        // bookIsbn 필드로도 검색 (실제 데이터 구조에 맞춰서)
        if (allHighlights.length === 0) {
          const bookIsbnQuery = query(
            collection(db, 'highlights'),
            where('bookIsbn', '==', bookIdentifier),
            limit(100)
          );
          const bookIsbnSnapshot = await getDocs(bookIsbnQuery);
          const bookIsbnHighlights = bookIsbnSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          allHighlights = [...allHighlights, ...bookIsbnHighlights];
          console.log(`[BookSearchDetail] Found ${bookIsbnHighlights.length} highlights with bookIsbn`);
        }
        
        // isbn 필드로도 검색 (실제 데이터 구조에 맞춰서)
        if (allHighlights.length === 0) {
          const isbnQuery = query(
            collection(db, 'highlights'),
            where('isbn', '==', bookIdentifier),
            limit(100)
          );
          const isbnSnapshot = await getDocs(isbnQuery);
          const isbnHighlights = isbnSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          allHighlights = [...allHighlights, ...isbnHighlights];
          console.log(`[BookSearchDetail] Found ${isbnHighlights.length} highlights with isbn`);
        }
        
        // 현재 사용자의 하이라이트는 제외 (다른 사람의 하이라이트만 표시)
        if (currentUser) {
          allHighlights = allHighlights.filter(highlight => highlight.uid !== currentUser.uid);
          console.log(`[BookSearchDetail] After filtering current user, ${allHighlights.length} highlights remaining`);
        }
        
        console.log(`[BookSearchDetail] Total highlights found: ${allHighlights.length}`);
        console.log(`[BookSearchDetail] Sample highlight:`, allHighlights[0]);
        
        // 중복 제거 (id 기준)
        const uniqueHighlights = allHighlights.filter((highlight, index, self) => 
          index === self.findIndex(h => h.id === highlight.id)
        );
        
        // 랜덤으로 5개 선택
        const shuffledHighlights = fisherYatesShuffle(uniqueHighlights);
        const selectedHighlights = shuffledHighlights.slice(0, 5);
        
        console.log(`[BookSearchDetail] Selected ${selectedHighlights.length} highlights for display`);
        setOtherHighlights(selectedHighlights);
        
        // 사용자 정보 가져오기
        const userIds = [...new Set(selectedHighlights.map(h => h.uid).filter(Boolean))];
        const userPromises = userIds.map(async (uid) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', uid));
            return { uid, data: userDoc.data() };
          } catch (error) {
            console.warn(`Failed to fetch user ${uid}:`, error);
            return { uid, data: null };
          }
        });
        
        const userResults = await Promise.all(userPromises);
        const newUserMap = {};
        userResults.forEach(({ uid, data }) => {
          if (data) {
            newUserMap[uid] = data.displayName || '익명';
          }
        });
        setUserMap(prev => ({ ...prev, ...newUserMap }));
        
              } catch (e) {
          console.error('Error fetching highlights:', e);
          setOtherHighlights([]);
          // Firebase 인덱스 오류인 경우 더 구체적인 메시지
          if (e.code === 'failed-precondition' || e.message.includes('index')) {
            console.warn("Firebase 인덱스 오류 감지, 단순화된 쿼리로 재시도...");
            try {
              // 모든 필드명으로 재시도
              const queries = [
                query(collection(db, 'highlights'), where('bookId', '==', bookIdentifier)),
                query(collection(db, 'highlights'), where('bookIsbn', '==', bookIdentifier)),
                query(collection(db, 'highlights'), where('isbn', '==', bookIdentifier))
              ];
              
              let allHighlights = [];
              for (const query of queries) {
                try {
                  const snapshot = await getDocs(query);
                  const highlights = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                  allHighlights = [...allHighlights, ...highlights];
                } catch (queryError) {
                  console.warn(`Query failed:`, queryError);
                }
              }
              
                             // 중복 제거
               const uniqueHighlights = allHighlights.filter((highlight, index, self) => 
                 index === self.findIndex(h => h.id === highlight.id)
               );
               
               // 현재 사용자의 하이라이트는 제외
               let filteredHighlights = uniqueHighlights;
               if (currentUser) {
                 filteredHighlights = uniqueHighlights.filter(highlight => highlight.uid !== currentUser.uid);
               }
               
               const shuffledSimpleHighlights = fisherYatesShuffle(filteredHighlights);
               const selectedSimpleHighlights = shuffledSimpleHighlights.slice(0, 5);
               setOtherHighlights(selectedSimpleHighlights);
               console.log(`[BookSearchDetail] Retry found ${selectedSimpleHighlights.length} highlights`);
            } catch (retryError) {
              console.error("재시도 실패:", retryError);
            }
          }
        } finally {
          setLoadingHighlights(false);
        }
    };
    fetchOtherHighlights();
  }, [book]);

  // 메모 가져오기 (books 컬렉션의 memo 필드에서 검색)
  useEffect(() => {
    const fetchOtherMemos = async () => {
      if (!book || !book.isbn) {
        setOtherMemos([]);
        setLoadingMemos(false);
        return;
      }
      setLoadingMemos(true);
      const bookIdentifier = (book.isbn || '').split(' ')[0];
      console.log(`[BookSearchDetail] Fetching memos from books collection for ISBN: ${bookIdentifier}`);
      
      try {
        // books 컬렉션에서 해당 ISBN을 가진 책들을 검색
        const booksQuery = query(
          collection(db, 'books'),
          where('isbn', '==', bookIdentifier),
          limit(100)
        );
        const booksSnapshot = await getDocs(booksQuery);
        let allBooksWithMemos = booksSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(bookData => bookData.memo && bookData.memo.trim() !== ''); // 메모가 있는 책만 필터링
        
        console.log(`[BookSearchDetail] Found ${allBooksWithMemos.length} books with memos for ISBN: ${bookIdentifier}`);
        
        // 메모 데이터를 하이라이트와 동일한 형태로 변환
        let memoData = allBooksWithMemos.map(bookData => ({
          id: bookData.id, // book document ID를 사용
          text: bookData.memo,
          uid: bookData.uid,
          bookId: bookData.isbn,
          bookTitle: bookData.title,
          bookAuthors: bookData.authors,
          bookThumbnail: bookData.thumbnail,
          userName: bookData.userName || '익명',
          createdAt: bookData.createdAt,
          updatedAt: bookData.updatedAt,
          type: 'memo' // 메모 타입 표시
        }));
        
        // 현재 사용자의 메모는 제외 (다른 사람의 메모만 표시)
        if (currentUser) {
          memoData = memoData.filter(memo => memo.uid !== currentUser.uid);
          console.log(`[BookSearchDetail] After filtering current user, ${memoData.length} memos remaining`);
        }
        
        console.log(`[BookSearchDetail] Total memos found: ${memoData.length}`);
        console.log(`[BookSearchDetail] Sample memo:`, memoData[0]);
        
        // 랜덤으로 5개 선택
        const shuffledMemos = fisherYatesShuffle(memoData);
        const selectedMemos = shuffledMemos.slice(0, 5);
        
        console.log(`[BookSearchDetail] Selected ${selectedMemos.length} memos for display`);
        setOtherMemos(selectedMemos);
        
        // 사용자 정보 가져오기 (메모용)
        const userIds = [...new Set(selectedMemos.map(m => m.uid).filter(Boolean))];
        const userPromises = userIds.map(async (uid) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', uid));
            return { uid, data: userDoc.data() };
          } catch (error) {
            console.warn(`Failed to fetch user ${uid}:`, error);
            return { uid, data: null };
          }
        });
        
        const userResults = await Promise.all(userPromises);
        const newUserMap = {};
        userResults.forEach(({ uid, data }) => {
          if (data) {
            newUserMap[uid] = data.displayName || '익명';
          }
        });
        setUserMap(prev => ({ ...prev, ...newUserMap }));
        
      } catch (e) {
        console.error('Error fetching memos:', e);
        setOtherMemos([]);
        // Firebase 인덱스 오류인 경우 더 구체적인 메시지
        if (e.code === 'failed-precondition' || e.message.includes('index')) {
          console.warn("Firebase 인덱스 오류 감지, 단순화된 쿼리로 재시도...");
          try {
            // 단순화된 쿼리로 재시도
            const simpleQuery = query(
              collection(db, 'books'),
              where('isbn', '==', bookIdentifier)
            );
            const simpleSnapshot = await getDocs(simpleQuery);
            const simpleBooksWithMemos = simpleSnapshot.docs
              .map(doc => ({ id: doc.id, ...doc.data() }))
              .filter(bookData => bookData.memo && bookData.memo.trim() !== '');
            
                         let simpleMemoData = simpleBooksWithMemos.map(bookData => ({
               id: bookData.id,
               text: bookData.memo,
               uid: bookData.uid,
               bookId: bookData.isbn,
               bookTitle: bookData.title,
               bookAuthors: bookData.authors,
               bookThumbnail: bookData.thumbnail,
               userName: bookData.userName || '익명',
               createdAt: bookData.createdAt,
               updatedAt: bookData.updatedAt,
               type: 'memo'
             }));
             
             // 현재 사용자의 메모는 제외
             if (currentUser) {
               simpleMemoData = simpleMemoData.filter(memo => memo.uid !== currentUser.uid);
             }
            
            const shuffledSimpleMemos = fisherYatesShuffle(simpleMemoData);
            const selectedSimpleMemos = shuffledSimpleMemos.slice(0, 5);
            setOtherMemos(selectedSimpleMemos);
            console.log(`[BookSearchDetail] Retry found ${selectedSimpleMemos.length} memos`);
          } catch (retryError) {
            console.error("재시도 실패:", retryError);
          }
        }
      } finally {
        setLoadingMemos(false);
      }
    };
    fetchOtherMemos();
  }, [book]);

  // 하이라이트/메모 카드 클릭 핸들러
  const handleHighlightCardClick = (item, type = 'highlight') => {
    const userCode = item.uid || item.userId;
    const isbn = item.bookId || item.bookIsbn || item.isbn || '';
    
    // 메모인 경우 다른 경로로 이동
    if (type === 'memo') {
      const memoPath = `/book/${userCode}_${isbn}?uid=${userCode}&memoId=${item.id}`;
      console.log(`[BookSearchDetail] Navigating to memo: ${memoPath}`);
      navigate(memoPath);
      return;
    }
    
    // 하이라이트인 경우 기존 로직
    const bookDetailPath = `/book/${userCode}_${isbn}?uid=${userCode}&highlightId=${item.id}`;
    console.log(`[BookSearchDetail] Navigating to highlight: ${bookDetailPath}`);
    
    // 스크롤 위치를 기억하기 위해 sessionStorage에 저장
    sessionStorage.setItem('scrollPosition', window.scrollY.toString());
    
    // 브라우저 호환성을 위한 전환 효과
    try {
      document.body.style.transition = 'opacity 0.2s ease-out';
      document.body.style.opacity = '0.8';
    } catch (error) {
      console.warn('Transition effect failed:', error);
    }
    
    // 페이지 이동
    navigate(bookDetailPath);
    
    // 전환 효과 복원
    setTimeout(() => {
      try {
        document.body.style.transition = '';
        document.body.style.opacity = '';
      } catch (error) {
        console.warn('Transition cleanup failed:', error);
      }
    }, 200);
  };

  const handleAddToLibrary = async () => {
    const user = currentUser;
    if (!user) {
      try {
        const result = await handleGoogleLogin(
          (user) => {
            console.log("Login successful, user needs to click 'Add' again.");
            setSnackbar({ open: true, message: '로그인 되었습니다. 다시 추가 버튼을 눌러주세요.', severity: 'info' });
          },
          (error) => {
            console.error('로그인 오류:', error);
            setSnackbar({ open: true, message: '로그인 중 오류가 발생했습니다.', severity: 'error' });
          },
          () => {
            setInAppBrowserModalOpen(true);
          }
        );
      } catch (error) {
        console.error('로그인 오류:', error);
        setSnackbar({ open: true, message: '로그인 중 오류가 발생했습니다.', severity: 'error' });
      }
      return;
    }
    
    if (!book || !book.isbn) {
      setSnackbar({ open: true, message: '책 정보를 찾을 수 없습니다.', severity: 'warning' });
      return; 
    }

    const cleanIsbn = (book.isbn || '').split(' ')[0];
    if (!cleanIsbn) { 
      setSnackbar({ open: true, message: '유효하지 않은 ISBN 정보입니다.', severity: 'warning' });
      return; 
    }

    setAddingToLibrary(true);
    const bookDocId = `${user.uid}_${cleanIsbn}`;
    const bookRef = doc(db, "books", bookDocId);

    try {
      const docSnap = await getDoc(bookRef);
      if (docSnap.exists()) {
        setSnackbar({ open: true, message: '이미 내 서재에 등록된 책입니다.', severity: 'info' });
        setIsInLibrary(true);
        setAddingToLibrary(false);
        return;
      }

      const booksCollectionRef = collection(db, "books");
      const countQuery = query(booksCollectionRef, where("isbn", "==", cleanIsbn));
      let currentOwnershipCount = 0;
      try {
        const countSnapshot = await getCountFromServer(countQuery);
        currentOwnershipCount = countSnapshot.data().count;
        console.log(`[BookSearchDetail] Current ownership count for ISBN ${cleanIsbn}: ${currentOwnershipCount}`);
      } catch (countError) {
         console.error(`[BookSearchDetail] Error fetching ownership count for ISBN ${cleanIsbn}:`, countError);
      }
      
      const bookData = {
        title: book.title || '제목 없음',
        authors: book.authors || [],
        publisher: book.publisher || '',
        thumbnail: book.thumbnail || '/images/no-cover.png',
        isbn: cleanIsbn,
        uid: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ownershipCount: currentOwnershipCount + 1,
        contents: book.contents || '',
        readStatus: 'not_started',
        startedAt: new Date(),
      };

      await setDoc(bookRef, bookData);
      console.log(`[BookSearchDetail] Book added for user ${user.uid} with ID ${bookDocId}`);

      setIsInLibrary(true);
      
      // 성공 토스트 메시지 표시
      setSnackbar({ 
        open: true, 
        message: '책이 내 서재에 추가되었습니다! 책 리뷰를 작성해보세요.', 
        severity: 'success' 
      });

      // 2초 후 MyPage의 BookDetail로 이동
      const timeoutId = setTimeout(() => {
        try {
          // 스크롤 위치를 기억하기 위해 sessionStorage에 저장
          sessionStorage.setItem('scrollPosition', window.scrollY.toString());
          
          // 부드러운 전환 효과
          document.body.style.transition = 'opacity 0.3s ease-out';
          document.body.style.opacity = '0.8';
          
          // MyPage의 BookMemoEdit로 이동
          navigate(`/book/${bookDocId}/memo-edit`);
          
          // 전환 효과 복원
          setTimeout(() => {
            try {
              document.body.style.transition = '';
              document.body.style.opacity = '';
            } catch (error) {
              console.warn('Transition cleanup failed:', error);
            }
          }, 300);
        } catch (navigationError) {
          console.error('Navigation error:', navigationError);
          // 네비게이션 실패 시 기본 경로로 이동
          navigate('/mybooks');
        }
      }, 2000);
      
      setNavigationTimeout(timeoutId);

    } catch (error) {
      console.error('책 추가 오류:', error);
      setSnackbar({ open: true, message: `책 추가 중 오류 발생: ${error.message}`, severity: 'error' });
    } finally {
      setAddingToLibrary(false);
    }
  };

  const renderAddButton = () => {
    if (loadingLibraryStatus) {
      return <Button size="small" disabled><CircularProgress size={20} /></Button>;
    }
    if (isInLibrary) {
      const bookDocId = currentUser ? `${currentUser.uid}_${(book.isbn || '').split(' ')[0]}` : null;
      return (
        <Tooltip title="내 서재에서 보기">
          <Button 
            variant="contained" 
            startIcon={<BookmarkAddedIcon />} 
            size="small"
            onClick={() => bookDocId && navigate(`/book/${bookDocId}`)} 
            disabled={!bookDocId}
            sx={{
              bgcolor: '#111',
              color: '#fff',
              '&:hover': {
                bgcolor: '#333',
              },
              '&:disabled': {
                bgcolor: '#ccc',
                color: '#666',
              }
            }}
          >
            내 서재에 있음
          </Button>
        </Tooltip>
      );
    }
    return (
      <Button 
        variant="contained" 
        startIcon={<BookmarkAddIcon />} 
        onClick={handleAddToLibrary} 
        disabled={addingToLibrary} 
        size="small"
        sx={{
          bgcolor: '#111',
          color: '#fff',
          '&:hover': {
            bgcolor: '#333',
          },
          '&:disabled': {
            bgcolor: '#ccc',
            color: '#666',
          }
        }}
      >
        {addingToLibrary ? '추가 중...' : '내 서재에 추가'}
      </Button>
    );
  };

  if (loading && !book) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
         <TopBar title="오류" showBack={true} />
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!book) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
         <TopBar title="정보 없음" showBack={true} />
        <Typography>책 정보를 찾을 수 없습니다.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 7, maxWidth: '600px', margin: '0 auto', width: '100%' }}>
      <TopBar 
        title={book.title || "책 정보"} 
        showBack={true}
      />



      <Box sx={{ p: 0 }}>
        <Card 
          sx={{ 
            mb: 0, 
            boxShadow: 'none',   
            borderRadius: 0,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
          }} 
          elevation={0}          
        >
          <CardContent sx={{ display: 'flex', gap: 2, pt: 4, width: '100%', maxWidth: '100%', minWidth: 0 }}>
            <Box
              component="img"
              src={book.thumbnail || '/images/no-cover.png'}
              alt={`${book.title}${book.authors ? ` - ${book.authors.join(', ')}` : ''}`}
              loading="lazy"
              sx={{
                width: 75,
                height: 108,
                objectFit: 'cover',
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderTopRightRadius: 10,
                borderBottomRightRadius: 10,
                boxShadow: '8px 4px 18px 0 rgba(0,0,0,0.18), 12px 12px 24px 0 rgba(0,0,0,0.10)'
              }}
            />
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '100%', minWidth: 0 }}>
              <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 'bold',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                width: '100%',
                maxWidth: '100%',
              }}>{book.title}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {book.authors?.join(', ') || '작가 정보 없음'}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'right'
                  }}
                >
                  {book.publisher || '출판사 정보 없음'}
                </Typography>
              </Box>
              {book.datetime && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  출간일: {new Date(book.datetime).toLocaleDateString()}
                </Typography>
              )}
              <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                {currentUser && renderAddButton()}
                {!currentUser && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={async () => {
                      try { await signInWithPopup(auth, googleProvider); }
                      catch(e) { console.error(e); }
                    }}
                    sx={{
                      bgcolor: '#111',
                      color: '#fff',
                      '&:hover': {
                        bgcolor: '#333',
                      }
                    }}
                  >
                    로그인 후 추가
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
          {book.contents && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                ml: 2,
                mr: 2,
                mt: 0.5,
                mb: 0.5,
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 3,         
                WebkitBoxOrient: 'vertical',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',       
                lineHeight: 1.6,            
                maxHeight: '4.8em',         
              }}
            >
              {book.contents}
            </Typography>
          )}
        </Card>
      </Box>

      {/* 다른 사람의 하이라이트 섹션 */}
      <Divider sx={{ my: 3 }} />
      <Box sx={{ px: 0 }}>
        <Typography variant="h6" sx={{
          px: 2,
          mb: 2,
          fontWeight: 600,
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'normal',
        }}>다른 사람의 문장</Typography>
        
        {loadingHighlights ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : memoizedOtherHighlights.length > 0 ? (
          <>
            {memoizedOtherHighlights.map((highlight, idx) => {
              const userCode = highlight.uid || highlight.userId;
              const isbn = highlight.bookId || highlight.bookIsbn || highlight.isbn || '';
              const userProfilePath = `/u/${userCode}`;

              return (
                <Card
                  key={highlight.id}
                  sx={{
                    mb: 2,
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    boxShadow: 'none',
                    border: 'none',
                    borderBottom: '1px solid #ececec',
                    transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      backgroundColor: '#f7f7f7',
                      transform: 'translateY(-1px)',
                    },
                    '&:active': {
                      transform: 'translateY(0px) scale(0.99)',
                    },
                    p: 0,
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                  }}
                  onClick={() => handleHighlightCardClick(highlight)}
                >
                  <CardContent sx={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    px: 2,
                    py: 2.5,
                  }}>
                    <Typography
                      variant="h7"
                      sx={{
                        fontWeight: 500,
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 5,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.7,
                      }}
                    >
                      {(highlight.text || '').replace(/\n/g, ' ')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', minHeight: 72 }}>
                      <CardMedia
                        component="img"
                        sx={{
                          width: 48,
                          height: 72,
                          objectFit: 'cover',
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          borderTopRightRadius: 6,
                          borderBottomRightRadius: 6,
                          boxShadow: '4px 2px 8px 0 rgba(0,0,0,0.10)',
                          alignSelf: 'stretch',
                          bgcolor: '#f5f5f5'
                        }}
                        image={highlight.bookThumbnail || book.thumbnail || '/images/no-cover.png'}
                        alt="책 표지"
                      />
                      <Box sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        height: 72
                      }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.92rem',
                            color: 'text.primary',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal',
                          }}
                        >
                          {highlight.bookTitle || book.title || '제목 없음'}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 0.5,
                            color: 'text.primary',
                            fontWeight: 400,
                            fontSize: '0.85em',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal',
                          }}
                        >
                          {highlight.bookAuthors?.join(', ') || book.authors?.join(', ') || '작가 정보 없음'}
                        </Typography>
                        <MuiLink
                          component={RouterLink}
                          to={userProfilePath}
                          data-nickname
                          underline="hover"
                          color="primary"
                          onClick={e => e.stopPropagation()}
                          sx={{
                            fontSize: '0.87em',
                            cursor: 'pointer',
                            fontWeight: 500,
                            transition: 'color 0.2s',
                            '&:hover': { color: 'primary.dark', textDecoration: 'underline' },
                          }}
                                                 >
                           {memoizedUserMap[highlight.uid] || highlight.userName || '익명'}님의 문장
                         </MuiLink>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </>
        ) : (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              다른 사용자의 문장이 없습니다.
            </Typography>
          </Box>
        )}
      </Box>

      {/* 다른 사람의 메모 섹션 */}
      <Divider sx={{ my: 3 }} />
      <Box sx={{ px: 0 }}>
        <Typography variant="h6" sx={{
          px: 2,
          mb: 2,
          fontWeight: 600,
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'normal',
        }}>다른 사람의 메모</Typography>
        
        {loadingMemos ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : memoizedOtherMemos.length > 0 ? (
          <>
            {memoizedOtherMemos.map((memo, idx) => {
              const userCode = memo.uid || memo.userId;
              const isbn = memo.bookId || memo.bookIsbn || memo.isbn || '';
              const userProfilePath = `/u/${userCode}`;

              return (
                <Card
                  key={memo.id}
                  sx={{
                    mb: 2,
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    boxShadow: 'none',
                    border: 'none',
                    borderBottom: '1px solid #ececec',
                    transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      backgroundColor: '#f7f7f7',
                      transform: 'translateY(-1px)',
                    },
                    '&:active': {
                      transform: 'translateY(0px) scale(0.99)',
                    },
                    p: 0,
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                  }}
                                     onClick={() => handleHighlightCardClick(memo, 'memo')}
                >
                  <CardContent sx={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    px: 2,
                    py: 2.5,
                  }}>
                    <Typography
                      variant="h7"
                      sx={{
                        fontWeight: 500,
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 5,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.7,
                      }}
                    >
                      {(memo.text || '').replace(/\n/g, ' ')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', minHeight: 72 }}>
                      <CardMedia
                        component="img"
                        sx={{
                          width: 48,
                          height: 72,
                          objectFit: 'cover',
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          borderTopRightRadius: 6,
                          borderBottomRightRadius: 6,
                          boxShadow: '4px 2px 8px 0 rgba(0,0,0,0.10)',
                          alignSelf: 'stretch',
                          bgcolor: '#f5f5f5'
                        }}
                        image={memo.bookThumbnail || book.thumbnail || '/images/no-cover.png'}
                        alt="책 표지"
                      />
                      <Box sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        height: 72
                      }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.92rem',
                            color: 'text.primary',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal',
                          }}
                        >
                          {memo.bookTitle || book.title || '제목 없음'}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 0.5,
                            color: 'text.primary',
                            fontWeight: 400,
                            fontSize: '0.85em',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal',
                          }}
                        >
                          {memo.bookAuthors?.join(', ') || book.authors?.join(', ') || '작가 정보 없음'}
                        </Typography>
                        <MuiLink
                          component={RouterLink}
                          to={userProfilePath}
                          data-nickname
                          underline="hover"
                          color="primary"
                          onClick={e => e.stopPropagation()}
                          sx={{
                            fontSize: '0.87em',
                            cursor: 'pointer',
                            fontWeight: 500,
                            transition: 'color 0.2s',
                            '&:hover': { color: 'primary.dark', textDecoration: 'underline' },
                          }}
                                                 >
                           {memoizedUserMap[memo.uid] || memo.userName || '익명'}님의 메모
                         </MuiLink>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </>
        ) : (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              다른 사용자의 메모가 없습니다.
            </Typography>
          </Box>
        )}
      </Box>

             <Snackbar
         open={snackbar.open}
         autoHideDuration={4000}
         onClose={() => {
           setSnackbar({ ...snackbar, open: false });
           // 자동 이동 취소
           if (navigationTimeout) {
             clearTimeout(navigationTimeout);
             setNavigationTimeout(null);
           }
         }}
         anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbar-root': {
            width: 'auto',
            maxWidth: '90vw',
          }
        }}
      >
                 <Paper
           elevation={8}
           sx={{
             backgroundColor: snackbar.severity === 'success' ? '#111' : 
                              snackbar.severity === 'error' ? '#d32f2f' :
                              snackbar.severity === 'warning' ? '#ed6c02' : '#1976d2',
             color: '#fff',
             borderRadius: '12px',
             padding: '12px 20px',
             minWidth: '280px',
             maxWidth: '90vw',
             boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
             border: snackbar.severity === 'success' ? '1px solid #FFD700' : 'none',
             position: 'relative',
             overflow: 'hidden',
             '&::before': snackbar.severity === 'success' ? {
               content: '""',
               position: 'absolute',
               top: 0,
               left: 0,
               right: 0,
               height: '2px',
               background: 'linear-gradient(90deg, #FFD700, #FFA000)',
             } : {},
           }}
         >
           <Box sx={{ 
             display: 'flex', 
             alignItems: 'center', 
             justifyContent: 'space-between',
             gap: 1,
             fontFamily: 'NanumSquareB, sans-serif',
             fontSize: '0.9rem',
             fontWeight: 500,
             letterSpacing: '-0.01em',
             lineHeight: 1.4,
           }}>
             <Typography
               variant="body2"
               sx={{
                 color: '#fff',
                 fontFamily: 'NanumSquareB, sans-serif',
                 fontSize: '0.9rem',
                 fontWeight: 500,
                 letterSpacing: '-0.01em',
                 lineHeight: 1.4,
                 textAlign: 'left',
                 flex: 1,
                 wordBreak: 'keep-all',
                 whiteSpace: 'pre-wrap',
                 pr: 1,
               }}
             >
               {snackbar.message}
             </Typography>
             <IconButton
               size="small"
               onClick={() => {
                 setSnackbar({ ...snackbar, open: false });
                 // 자동 이동 취소
                 if (navigationTimeout) {
                   clearTimeout(navigationTimeout);
                   setNavigationTimeout(null);
                 }
               }}
               sx={{
                 color: '#fff',
                 p: 0.5,
                 minWidth: 'auto',
                 '&:hover': {
                   backgroundColor: 'rgba(255, 255, 255, 0.1)',
                 },
                 '&:active': {
                   backgroundColor: 'rgba(255, 255, 255, 0.2)',
                 },
               }}
             >
               <Box
                 component="span"
                 sx={{
                   fontSize: '16px',
                   fontWeight: 'bold',
                   lineHeight: 1,
                 }}
               >
                 ×
               </Box>
             </IconButton>
           </Box>
         </Paper>
      </Snackbar>

      <InAppBrowserModal 
        open={inAppBrowserModalOpen} 
        onClose={() => setInAppBrowserModalOpen(false)} 
      />

    </Box>
);
}

export default BookSearchDetail; 